// tslint:disable no-else-after-return

import { action, computed, observable, makeObservable } from 'mobx';
import { constructNewPolicyJacketUI, IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { ErrorService } from './ErrorService';
import { deletePolicyJacketAsync, updatePolicyNumberAsync, uploadPolicyJacketAsync } from './PolicyServiceRequest';
import getConfig from '../utils/getConfig';
import { IPolicyNumberError } from '../classes/IPolicyNumberError';
import { AxiosError } from 'axios';

export default class PolicyJacketService {
  private policyJacket: IPolicyJacketUI = observable.object(constructNewPolicyJacketUI());

  private policyError = new ErrorService();

  private initPolicyNumber: string | null = null;

  private policyNumberRequired = true;

  constructor() {
    makeObservable<
      PolicyJacketService,
      'policyError' | 'getJacketError' | 'initPolicyNumber' | 'policyNumberRequired'
    >(this, {
      policyError: observable,
      initPolicyNumber: observable,
      policyNumberRequired: observable,
      setPolicyJacket: action,
      uploadPolicyJacket: action,
      savePolicyJacket: action,
      deletePolicyJacket: action,
      cleanPolicyJacket: action,
      setFile: action,
      setFileName: action,
      setFileLink: action,
      setPolicyNumber: action,
      savePolicyNumber: action,
      getJacketError: action,
      setPolicyCategory: action,
      getPolicyJacket: computed,
      getPolicyCategory: computed,
      policyJacketWasChanged: computed,
      policyJacketExist: computed,
      policyNumberWasChanged: computed,
      getPolicyJacketIsValid: computed,
      policyJacketReadyToSave: computed,
      getPolicyErrors: computed,
    });
  }

  public setPolicyJacket(jacket: IPolicyJacketUI) {
    this.setPolicyNumber(jacket.policyNumber);
    this.initPolicyNumber = jacket.policyNumber;
    this.setFileName(jacket.jacketName);
    this.policyJacket.latestJacketLink = jacket.latestJacketLink;
    this.policyJacket.policyId = jacket.policyId;
  }

  public uploadPolicyJacket(files: FileList | null) {
    if (files) {
      this.setFile(files[0]);
      this.setFileName(files[0].name);
    } else {
      this.setFile(null);
      this.setFileName(null);
      this.setFileLink(null);
    }
  }

  public savePolicyJacket = async (orderId: string) => {
    if (this.getPolicyJacketIsValid) {
      return uploadPolicyJacketAsync(this.getPolicyJacket.file!, this.getPolicyJacket, orderId)
        .then((responce) => {
          return Promise.resolve();
        })
        .catch((error) => {
          return Promise.reject(this.getJacketError(error));
        });
    }
    return Promise.reject('Form is not completed');
  };

  public deletePolicyJacket = async (policyType: PolicyTypeEnum, orderId: string) => {
    if (this.getPolicyJacket.latestJacketLink) {
      return deletePolicyJacketAsync(policyType, orderId)
        .then(() => {
          this.cleanPolicyJacket();
          return Promise.resolve();
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    } else {
      this.cleanPolicyJacket();
      return Promise.resolve();
    }
  };

  public cleanPolicyJacket() {
    this.uploadPolicyJacket(null);
  }

  public setFile(file: any) {
    this.policyJacket.file = file;
  }

  public setFileName(name: string | null) {
    this.policyJacket.jacketName = name;
  }

  public setFileLink(link: string | null) {
    this.policyJacket.latestJacketLink = link;
  }

  public setPolicyNumber(name: string | null, error?: string | null) {
    this.policyJacket.policyNumber = name;
    this.policyError.setError('policyNumber', error);
  }

  public savePolicyNumber = async (orderId: string) => {
    if (this.getPolicyJacket.policyNumber) {
      return updatePolicyNumberAsync(this.getPolicyJacket, orderId)
        .then((responce) => {
          this.initPolicyNumber = this.getPolicyJacket.policyNumber;
          return Promise.resolve();
        })
        .catch((error) => {
          return Promise.reject(this.getJacketError(error));
        });
    }
    return Promise.reject('Policy number is not defined');
  };

  private getJacketError(e: AxiosError): string | IPolicyNumberError {
    if (typeof e === 'string') {
      return e;
    } else if (e.response && e.response.status === 409) {
      return e.response.data as IPolicyNumberError;
    } else {
      return e.response ? e.response.statusText : 'Internal Service Error';
    }
  }

  public setPolicyCategory(category: PolicyTypeEnum) {
    this.policyJacket.policyCategory = category;
    this.policyNumberRequired = getConfig(`config.policy.policyNumberRequired.${category}`);
  }

  public get getPolicyJacket(): IPolicyJacketUI {
    return this.policyJacket;
  }

  public get getPolicyCategory(): string | null {
    return this.policyJacket.policyCategory;
  }

  public get policyJacketWasChanged(): boolean {
    return this.policyJacket.file !== null || this.policyJacket.policyNumber !== null;
  }

  public get policyJacketExist(): boolean {
    return !!this.policyJacket.latestJacketLink || !!this.policyJacket.file;
  }

  public get policyNumberWasChanged(): boolean {
    return this.policyJacket.policyNumber !== this.initPolicyNumber;
  }

  public get getPolicyJacketIsValid(): boolean {
    const hasFileOrLink = !!this.policyJacket.latestJacketLink || !!this.policyJacket.file;
    return hasFileOrLink && this.policyNumberIsValid;
  }

  private get policyNumberIsValid(): boolean {
    return !(this.policyNumberRequired && this.policyJacket.policyNumber === null);
  }

  public get policyJacketReadyToSave(): boolean {
    return (
      this.policyJacketExist &&
      this.policyNumberRequired &&
      this.policyJacket.policyNumber !== null &&
      this.policyNumberWasChanged
    );
  }

  public get getPolicyErrors(): object {
    return this.policyError.getErrors;
  }
}
